import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog as MuiDialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import styled from 'styled-components';

import { ReactComponent as BinIcon } from '@/assets/icons/bin.svg';
import { ReactComponent as CloseDialogIcon } from '@/assets/icons/close-dialog-icon.svg';
import { MappingTemplate } from '@/features/canvas/api/import';
import { LinkageColumn, LinkageRow, LinkedColumn } from '@/features/canvas/types/linkage';
import { hasValidationErrors } from '@/features/canvas/utils/csvValidation';

import { FieldLinkingSelectionDialog } from './FieldLinkingSelectionDialog';
import { LinkingTable } from './LinkingTable';

type LinkingDialogProps = Omit<DialogProps, 'onSubmit'> & {
  columns: LinkageColumn[];
  linkedColumns: LinkedColumn[];
  onSubmit: (editedValues: Record<string, Record<string, string>>) => void;
  rows: LinkageRow[];
  setLinkedColumns: Dispatch<SetStateAction<LinkedColumn[]>>;
  editedValues?: Record<string, Record<string, string>>;
  onApplyTemplate?: (templateId: number) => void;
  onClose?: () => void;
  onDeleteTemplate?: (templateId: number) => void;
  onSaveTemplate?: (name: string) => void;
  setEditedValues?: Dispatch<SetStateAction<Record<string, Record<string, string>>>>;
  templates?: MappingTemplate[];
};

const LinkingDialog = ({
  open,
  onClose,
  rows,
  columns,
  linkedColumns,
  setLinkedColumns,
  onSubmit,
  templates = [],
  onApplyTemplate,
  onSaveTemplate,
  onDeleteTemplate,
  editedValues: parentEditedValues,
  setEditedValues: parentSetEditedValues,
  ...other
}: LinkingDialogProps) => {
  const { t } = useTranslation();

  const [isFieldLinkingDialogOpen, setIsFieldLinkingDialogOpen] = useState(false);
  const [linkingColumnId, setLinkingColumnId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  // Use parent-provided state if available, otherwise use local state
  const [localEditedValues, setLocalEditedValues] = useState<
    Record<string, Record<string, string>>
  >({});
  const editedValues = parentEditedValues ?? localEditedValues;
  const setEditedValues = parentSetEditedValues ?? setLocalEditedValues;

  // Check if there are any validation errors
  const hasAnyValidationErrors = hasValidationErrors(rows, linkedColumns, columns, editedValues);

  const handleTemplateChange = (event: SelectChangeEvent) => {
    const templateId = event.target.value;
    setSelectedTemplate(templateId);

    if (templateId && onApplyTemplate) {
      onApplyTemplate(Number(templateId));
    }
  };

  return (
    <MuiDialog
      open={open}
      sx={{
        '& .MuiDialog-paper': {
          padding: '2rem 2rem 4rem 2rem',
          borderRadius: '2rem',
          minWidth: '900px',
          overflowY: 'unset',
        },
      }}
      {...other}
    >
      <StyledDialogTitle>
        {t('editor.sidebar.data_tab.linking_dialog_title')}
        {onClose && (
          <StyledCloseWrapper onClick={() => onClose()}>
            <CloseDialogIcon />
          </StyledCloseWrapper>
        )}
      </StyledDialogTitle>
      <DialogSubtitle>
        <span>{t('editor.sidebar.data_tab.linking_dialog_subtitle')}</span>
        {hasAnyValidationErrors && (
          <span style={{ color: '#d32f2f', marginLeft: 'auto' }}>
            {t('editor.sidebar.data_tab.validation_error_message')}
          </span>
        )}
      </DialogSubtitle>

      {/* Template Selector */}
      {templates.length > 0 && (
        <TemplateSelector>
          <StyledFormControl size="small">
            <InputLabel id="template-select-label">
              {t('editor.sidebar.data_tab.template_selector_label', 'Template')}
            </InputLabel>
            <Select
              labelId="template-select-label"
              id="template-select"
              value={selectedTemplate}
              label={t('editor.sidebar.data_tab.template_selector_label', 'Template')}
              onChange={handleTemplateChange}
            >
              <MenuItem value="">
                <em>
                  {t('editor.sidebar.data_tab.template_none_auto_detect', 'None (Auto-detect)')}
                </em>
              </MenuItem>
              {[...templates]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(template => (
                  <MenuItem key={template.id} value={template.id.toString()}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <span>{template.name}</span>
                      {onDeleteTemplate && (
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            onDeleteTemplate(template.id);
                            setSelectedTemplate('');
                          }}
                          sx={{
                            ml: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 0, 0, 0.1)',
                            },
                          }}
                        >
                          <BinIcon style={{ width: 16, height: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </MenuItem>
                ))}
            </Select>
          </StyledFormControl>
        </TemplateSelector>
      )}

      <DialogContent sx={{ padding: 0 }}>
        <LinkingTable
          dataColumns={columns}
          dataRows={rows}
          linkedColumns={linkedColumns}
          setIsFieldLinkingDialogOpen={setIsFieldLinkingDialogOpen}
          setLinkingColumnId={setLinkingColumnId}
          setLinkedColumns={setLinkedColumns}
          onSubmit={onSubmit}
          onSaveTemplate={onSaveTemplate}
          editedValues={editedValues}
          setEditedValues={setEditedValues}
        />
      </DialogContent>
      <FieldLinkingSelectionDialog
        open={isFieldLinkingDialogOpen}
        onClose={() => {
          setLinkingColumnId(null);
          setIsFieldLinkingDialogOpen(false);
        }}
        linkingColumnId={linkingColumnId}
        linkedColumns={linkedColumns}
        setLinkedColumns={setLinkedColumns}
        dataRows={rows}
        indexOfSelectedOptionId={columns.findIndex(c => c.id === linkingColumnId)}
      />
    </MuiDialog>
  );
};

const StyledDialogTitle = styled(DialogTitle)`
  font-size: 1.75rem;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  font-weight: 600;
  margin-bottom: 0.5rem;
  padding: 0;
`;

const DialogSubtitle = styled.div`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledCloseWrapper = styled(Box)`
  cursor: pointer;
  top: -2rem;
  right: -2rem;
  position: absolute;
  border-radius: 2rem;
  width: 5rem;
  height: 5rem;
  box-shadow: ${({ theme }) => theme.shadows[0]};
  background-color: ${({ theme }) => theme.palette.brand.white};
  z-index: 1;

  svg {
    width: 100%;
    height: 100%;

    path {
      fill: ${({ theme }) => theme.palette.brand.primary};
    }
  }
`;

const TemplateSelector = styled.div`
  margin-bottom: 2rem;
  width: 100%;
`;

const StyledFormControl = styled(FormControl)`
  min-width: 250px;

  & .MuiInputLabel-root {
    font-size: 1.4rem;
  }

  & .MuiSelect-select {
    font-size: 1.4rem;
  }

  & .MuiMenuItem-root {
    font-size: 1.3rem;
  }
`;

export { LinkingDialog };
